import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

export default function ManifestoChapter({ chapter, index }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const flip = index % 2 === 1;
  const hasVideo = Boolean(chapter.video);

  return (
    <section
      id={chapter.id}
      ref={ref}
      data-testid={`chapter-${chapter.id}`}
      className="relative border-t border-[#2C2C2C]"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-40">
        <div className="grid grid-cols-12 gap-6 md:gap-10">
          <div className={`col-span-12 md:col-span-5 ${flip ? "md:order-2" : ""}`}>
            <div className="md:sticky md:top-32">
              <div className="flex items-baseline gap-4 mb-6">
                <span className="chapter-num text-[15vw] md:text-[10vw] leading-none text-[#C67022]">
                  {chapter.number}
                </span>
                <span className="font-mono text-[10px] tracking-[0.3em] text-white/40 uppercase">
                  / Chapter
                </span>
              </div>
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                className="font-display font-black text-6xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9]"
              >
                {chapter.title}
              </motion.h2>
              <div className="mt-4 font-display italic text-2xl md:text-3xl text-white/50">
                {chapter.kicker}
              </div>
              <div className="mt-10 flex flex-wrap gap-2">
                {(chapter.tags || []).map((t) => (
                  <span
                    key={t}
                    className="font-mono text-[10px] tracking-[0.25em] uppercase px-3 py-1.5 border border-[#2C2C2C] text-white/60"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className={`col-span-12 md:col-span-7 ${flip ? "md:order-1" : ""}`}>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="relative"
            >
              <div className="relative overflow-hidden aspect-[2.35/1] md:aspect-[16/9] bg-[#121212] vignette">
                {hasVideo ? (
                  <motion.video
                    style={{ y: imageY }}
                    src={chapter.video}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="w-full h-full object-cover grayscale hover:grayscale-0 transition-[filter] duration-1000 will-change-transform scale-110"
                  />
                ) : (
                  <motion.img
                    style={{ y: imageY }}
                    src={chapter.image}
                    alt={chapter.title}
                    className="w-full h-full object-cover grayscale hover:grayscale-0 transition-[filter] duration-1000 will-change-transform scale-110"
                  />
                )}
                <div className="absolute top-0 left-0 px-4 py-3 bg-[#080808] font-mono text-[10px] tracking-[0.3em] text-[#C67022] border-r border-b border-[#2C2C2C]">
                  {chapter.number} / {chapter.title.toUpperCase()}
                </div>
                <div className="absolute bottom-0 right-0 px-4 py-3 bg-[#080808] font-mono text-[10px] tracking-[0.3em] text-white/50 border-l border-t border-[#2C2C2C]">
                  ROLL {index + 1}
                </div>
              </div>

              <div className="mt-12 md:mt-16 space-y-6 md:pl-16 border-l border-[#2C2C2C] pl-6">
                {(chapter.copy || []).map((p, i) => (
                  <motion.p
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, delay: 0.1 * i }}
                    className="text-lg md:text-xl leading-relaxed text-white/75 font-light"
                  >
                    <span className="text-[#C67022] font-mono text-xs align-top mr-3">
                      §{String(i + 1).padStart(2, "0")}
                    </span>
                    {p}
                  </motion.p>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
